import { Badge, Card, Group, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { DataTable } from "./DataTable";
import { GenerationBadge } from "./GenerationBadge";
import { MigrationStateCell } from "./MigrationStall";
import { UpgradeOfferActions, UpgradeOfferSummary } from "./UpgradeOfferPanel";
import { RelatedLink } from "./RelatedLink";
import { Timestamp } from "./Timestamp";
import { EmptyState, ErrorState } from "./states";
import { col } from "@/entities/columns";
import { COLLECTIONS } from "@/firestore/collections";
import { useFirestoreDoc, useFirestoreQuery } from "@/firestore/hooks";
import type {
  WalletDoc,
  WalletMigrationDoc,
  WalletUpgradeOfferDoc,
  WithDocId,
} from "@/firestore/types";
import { useNow } from "@/lib/useNow";
import { entityPath } from "@/routes/paths";
import { canOfferFixedGeneration } from "@/wallet/generations";
import { migrationRelation } from "@/wallet/migrations";

type Row = WithDocId<WalletMigrationDoc>;

const RELATION_LABEL = {
  from: { label: "From this wallet", color: "blue" },
  to: { label: "To this wallet", color: "teal" },
  other: { label: "Owner's other wallet", color: "gray" },
} as const;

function columns(walletId: string, now: number) {
  return [
    col<Row>({
      id: "id",
      header: "Migration",
      value: (r) => r.id ?? r._docId,
      cell: (r) => <RelatedLink entity="walletMigration" id={r._docId} />,
    }),
    col<Row>({
      id: "state",
      header: "State",
      value: (r) => r.state,
      cell: (r) => <MigrationStateCell migration={r} now={now} />,
    }),
    col<Row>({
      id: "relation",
      header: "Relation",
      value: (r) => migrationRelation(r, walletId),
      cell: (r) => {
        const relation = RELATION_LABEL[migrationRelation(r, walletId)];
        return (
          <Badge color={relation.color} variant="light" radius="sm" tt="none">
            {relation.label}
          </Badge>
        );
      },
    }),
    col<Row>({
      id: "generations",
      header: "Generations",
      value: (r) => `${r.fromGeneration} → ${r.toGeneration}`,
      cell: (r) => (
        <Group gap={4} wrap="nowrap">
          <GenerationBadge generation={r.fromGeneration} />
          <Text size="sm">→</Text>
          <GenerationBadge generation={r.toGeneration} />
        </Group>
      ),
    }),
    col<Row>({
      id: "rotation",
      header: "Owner rotation",
      value: (r) => (r.ownerRotation ? "yes" : "no"),
      cell: (r) => <Text size="sm">{r.ownerRotation ? "yes" : "no"}</Text>,
    }),
    col<Row>({
      id: "old",
      header: "From wallet",
      value: (r) => r.oldWallet,
      cell: (r) => <RelatedLink entity="wallet" id={r.oldWallet} />,
    }),
    col<Row>({
      id: "new",
      header: "To wallet",
      value: (r) => r.newWallet ?? "",
      cell: (r) => <RelatedLink entity="wallet" id={r.newWallet} />,
    }),
    col<Row>({
      id: "createdAt",
      header: "Created",
      value: (r) => r.createdAt,
      cell: (r) => <Timestamp value={r.createdAt} />,
    }),
    col<Row>({
      id: "updatedAt",
      header: "Updated",
      value: (r) => r.updatedAt,
      cell: (r) => <Timestamp value={r.updatedAt} />,
    }),
    col<Row>({
      id: "completedAt",
      header: "Completed",
      value: (r) => r.completedAt ?? "",
      cell: (r) => <Timestamp value={r.completedAt} />,
    }),
  ];
}

/**
 * Wallet 360's Migrations panel: every migration of the wallet's owner (newest first), so the
 * migration that created this wallet, the one leaving it and the owner's other attempts are all in
 * one place. Live on `walletMigrations (owner, createdAt desc)` — the mobile listener's index.
 *
 * The header carries the case-by-case upgrade commands (Offer / Withdraw, wallet-service 0.75.0),
 * and the wallet's offer (`walletUpgradeOffers/{id}`) heads the panel.
 */
export function WalletMigrationsCard({
  wallet,
  generation,
}: {
  wallet: WithDocId<WalletDoc>;
  generation: string | null;
}) {
  const navigate = useNavigate();
  const now = useNow();
  const migrations = useFirestoreQuery<WalletMigrationDoc>(COLLECTIONS.walletMigrations, {
    filters: [["owner", "==", wallet.owner]],
    orderBy: [["createdAt", "desc"]],
    limit: 50,
  });
  const offer = useFirestoreDoc<WalletUpgradeOfferDoc>(COLLECTIONS.walletUpgradeOffers, wallet.id);

  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Text fw={600}>Migrations</Text>
        <UpgradeOfferActions wallet={wallet} generation={generation} offer={offer} />
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        This owner&apos;s wallet migrations, newest first. Open one for its steps and the operator
        commands (redrive, abandon, reactivate). A v3.7 wallet may stay on v3.7: an upgrade starts
        only once offered here, then accepted and signed by the owner.
      </Text>
      {canOfferFixedGeneration(generation) || generation === null || offer.data ? (
        <Card withBorder radius="sm" padding="sm" mb="md">
          <UpgradeOfferSummary wallet={wallet} offer={offer} />
        </Card>
      ) : null}
      {migrations.error ? (
        <ErrorState error={migrations.error} />
      ) : (
        <DataTable
          data={migrations.data}
          columns={columns(wallet.id, now)}
          loading={migrations.loading}
          searchable={false}
          onRowClick={(row) => navigate(entityPath("walletMigration", row._docId))}
          emptyState={
            <EmptyState
              title="No migrations"
              description="This owner has never migrated a wallet."
            />
          }
        />
      )}
    </Card>
  );
}
